import * as React from 'react';
import ReactTable from 'react-table';
import checkboxHOC from 'react-table/lib/hoc/selectTable';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import classnames from 'classnames';

import Pagination from './Pagination';

import 'react-table-hoc-fixed-columns/lib/styles.css';
import './style.scss';

interface IManualProps {
  manual: boolean;
  records: number;
  onFetchData: (params: IServerPaginationParams) => void;
  params?: IServerPaginationParams;
}

interface ITableProps {
  columns: any;
  rows: any;
  onRowClick?: (event: any) => void;
  noDataText?: string;
  rowSelection?: {
    showCheckbox: boolean;
    selectIndex: string;
    onRowsToggle: (rows: any) => void;
    selectedIndexes?: any[];
  };
  pageSize?: number;
  customTopBar?: any;
  showPaginationTop?: boolean;
  showPaginationInFooter?: boolean;
  manualProps?: IManualProps;
  alternateColoredRows?: boolean;
  plainTable?: boolean;
  disabled?: boolean;
  PaginationComponent?: React.ReactType;
  showPagination?: boolean;
  resizable?: boolean;
  defaultSorted?: Array<{ id: string; desc: boolean }>;
  onPageChange?: (page: number) => void;
  SubComponent?: (row: any) => JSX.Element;
  calculatedSortedFromState?: boolean;
  sorted?: ITableSort;
  onSortedChange?: (sorted: any) => void;
  isFullPageTable?: boolean;
  loading?: boolean;
  isTableScrollble?: boolean;
}

interface ITableState {
  selectAll: boolean;
}

const CheckboxTable = checkboxHOC(ReactTable);
const ReactTableFixedColumns = withFixedColumns(ReactTable);

const TableLoading: React.FC = () => (
  <div style={{ position: 'absolute', top: '65%', left: '47%' }}>
    <img src="/assets/loading.gif" height={60} />
  </div>
);

export default class Table extends React.Component<ITableProps, ITableState> {
  static defaultProps: ITableProps = {
    columns: [],
    rows: [],
    onRowClick: () => null,
    rowSelection: {
      showCheckbox: false,
      selectIndex: null,
      onRowsToggle: () => null,
      selectedIndexes: [],
    },
    noDataText: 'No rows found',
    customTopBar: null,
    pageSize: 10,
    showPaginationTop: false,
    showPaginationInFooter: false,
    alternateColoredRows: false,
    PaginationComponent: Pagination,
    showPagination: true,
    disabled: false,
    resizable: true,
    defaultSorted: [],
    onPageChange: () => null,
    SubComponent: undefined,
    calculatedSortedFromState: true,
  };

  private tableRef: any = null;

  constructor(props: ITableProps) {
    super(props);

    this.state = {
      selectAll: false,
    };
  }

  componentDidMount() {
    const isCheckedTable = this.props.rowSelection && this.props.rowSelection.showCheckbox;

    if (isCheckedTable) {
      this.setState({
        selectAll: this.getSelectAllStatusFromProps(this.props),
      });
    }
  }

  componentDidUpdate(prevProps: Readonly<ITableProps>, prevState: Readonly<ITableState>, snapshot?: any): void {
    const nextProps = this.props;
    const isManual = nextProps.manualProps?.manual;
    const prevParams = prevProps.manualProps?.params;
    const hasRowsChanged = prevProps.rows !== nextProps.rows;
    const isCheckedTable = nextProps.rowSelection?.showCheckbox;
    const hasSelectedIndicesChanged =
      this.getSelectedIndicesFromProps(prevProps) !== this.getSelectedIndicesFromProps(nextProps);

    if ((isManual && nextProps.manualProps.params !== prevParams) || nextProps.sorted !== prevProps.sorted) {
      // Changed from false to passing the variable (CYBERD-3166)
      this.onManualFetchData(this.tableRef.state, nextProps, nextProps.calculatedSortedFromState);
    }

    if (isCheckedTable && (hasRowsChanged || hasSelectedIndicesChanged)) {
      this.setState({
        selectAll: this.getSelectAllStatusFromProps(nextProps),
      });
    }

    if (hasRowsChanged) {
      document.querySelectorAll('.rt-table').forEach((element) => {
        element.scrollTop = 0;
      });
    }
  }

  // Checkbox table methods.
  getSelectAllStatusFromProps = (nextProps: ITableProps) => {
    const selectedIndexes = this.getSelectedIndicesFromProps(nextProps);
    // Needed changes for CYB-3291
    const activeRows = nextProps.rows.filter((e) => e.hasOwnProperty('isActive') && e.isActive);
    const rows = activeRows || [];

    return rows.length ? rows.length === selectedIndexes.length : !!rows.length;
  };

  getSelectedIndicesFromProps = (nextProps: ITableProps) => {
    const { rowSelection } = nextProps;
    const selectedIndices = rowSelection && rowSelection.selectedIndexes ? rowSelection.selectedIndexes : [];

    return selectedIndices;
  };

  toggleSelection = (key, shift, row: any) => {
    const modifiedKey = parseInt(key.replace('select-', ''), 10);
    let selectedIds = [...this.getSelectedIndicesFromProps(this.props)];
    const keyIndex = selectedIds.indexOf(modifiedKey);

    // Needed changes for CYB-3291
    if (row.hasOwnProperty('isActive') && !row.isActive) {
      return;
    }

    if (keyIndex >= 0) {
      selectedIds = [...selectedIds.slice(0, keyIndex), ...selectedIds.slice(keyIndex + 1)];
    } else {
      selectedIds.push(modifiedKey);
    }

    this.props.rowSelection.onRowsToggle(selectedIds);
  };

  toggleAll = () => {
    const wrappedInstance = this.tableRef.getWrappedInstance();
    const currentRecords = wrappedInstance.getResolvedState().sortedData;
    const selectAll = !this.state.selectAll;
    const selectedIds = [];

    if (selectAll) {
      currentRecords.forEach((item, index) => {
        // Needed changes for CYB-3291
        if (item._original.hasOwnProperty('isActive') && item._original.isActive) {
          selectedIds.push(item._original[this.props.rowSelection.selectIndex]);
        }
      });
    }

    this.props.rowSelection.onRowsToggle(selectedIds);
  };

  getRows = (): any[] => this.props.rows || [];

  getData = () => {
    const data = this.getRows().map((item, index) => ({
      _id: item[this.props.rowSelection.selectIndex],
      ...item,
    }));

    return data;
  };

  isSelected = (key) => {
    const selectedIds = this.getSelectedIndicesFromProps(this.props);

    return selectedIds.indexOf(key) !== -1;
  };

  // manual pagination
  onManualFetchData = (state, props, calculateFromState: boolean = true) => {
    const offset = state.page * state.pageSize;
    const limit = state.pageSize;
    const { manualProps, calculatedSortedFromState, sorted } = props;
    let params: IServerPaginationParams = {};

    // pagination and sorting
    // handled from table component,
    // rest from parent component.
    if (manualProps && manualProps.params) {
      params = {
        ...manualProps.params,
      };
    }

    if (calculateFromState) {
      params.limit = limit;
      params.offset = offset;
    } else {
      // TODO: update the table state
      // from props
    }

    if (calculateFromState && calculatedSortedFromState && state.sorted.length > 0) {
      const sortBy = state.sorted[0];

      params.sortBy = sortBy.id;
      params.sortOrder = sortBy.desc ? SortOrder.DESC : SortOrder.ASC;
    } else {
      // TODO: update the table state
      // from props

      const sortBy = sorted;

      params.sortBy = sortBy ? sortBy.id : undefined;
      params.sortOrder = sortBy ? (sortBy.desc ? SortOrder.DESC : SortOrder.ASC) : undefined;
    }

    this.props.manualProps.onFetchData(params);
  };

  isShowPaginationBottom = (): boolean => {
    const { plainTable, rows, pageSize, showPaginationTop, showPaginationInFooter } = this.props;

    return showPaginationInFooter || (plainTable ? rows.length > pageSize : !showPaginationTop);
  };

  renderScrollableTable = (plainTable: boolean, alternateColoredRows: boolean, commonProps, manualProps) => {
    const element = (
      <ReactTableFixedColumns
        className={classnames({
          '-plain': plainTable,
          '-striped': alternateColoredRows,
        })}
        {...commonProps}
        {...manualProps}
        data={this.props.rows}
        getTrProps={(state, rowInfo, column, instance) => ({
          onClick: (e, handleOriginal) => this.props.onRowClick && this.props.onRowClick(rowInfo),
          className: state.expanded[rowInfo.viewIndex] ? 'rt-tr-expanded' : '',
        })}
        innerRef={(r) => (this.tableRef = r)}
        onPageChange={this.props.onPageChange}
      />
    );

    return this.props.isTableScrollble ? <div className="scrollable-table">{element}</div> : element;
  };

  renderTable() {
    const {
      PaginationComponent,
      columns,
      pageSize,
      noDataText,
      showPaginationTop,
      alternateColoredRows,
      plainTable,
      showPagination,
      resizable,
      defaultSorted,
      SubComponent,
      onSortedChange,
      loading,
    } = this.props;
    const commonProps = {
      PaginationComponent,
      columns,
      pageSize,
      noDataText,
      loading,
      minRows: 0,
      showPaginationTop,
      showPaginationBottom: this.isShowPaginationBottom(),
      showPagination,
      resizable,
      onSortedChange,
      ...(SubComponent ? { SubComponent } : {}),
      LoadingComponent: () => (loading ? <TableLoading /> : null),
    };
    const rowSelectionProps = {
      isSelected: this.isSelected,
      toggleAll: this.toggleAll,
      toggleSelection: this.toggleSelection,
      selectType: 'checkbox' as any,
      selectAll: this.state.selectAll,
    };
    const manualProps =
      this.props.manualProps && this.props.manualProps.manual
        ? {
            manual: this.props.manualProps.manual,
            onFetchData: (state, instance) => this.onManualFetchData(state, this.props),
            pages: Math.ceil(this.props.manualProps.records / this.props.pageSize),
            defaultSorted,
          }
        : {};

    return this.props.rowSelection.showCheckbox ? (
      <CheckboxTable
        className={classnames('table--checked', {
          '-plain': plainTable,
          '-striped': alternateColoredRows,
        })}
        {...commonProps}
        {...rowSelectionProps}
        {...manualProps}
        data={this.getData()}
        getTrProps={(state, rowInfo, column, instance) => {
          const isRowSelected = this.isSelected(rowInfo.original._id);

          return {
            onClick: (e, handleOriginal) => this.props.onRowClick && this.props.onRowClick(rowInfo),
            style: {
              background: isRowSelected ? '#f6fcff' : '',
              borderBottom: isRowSelected ? '1px solid #b9d9ef' : '',
            },
            // Needed changes for CYB-3291
            className:
              rowInfo.original.hasOwnProperty('isActive') && !rowInfo.original.isActive ? 'rt-tr-disabled' : '',
          };
        }}
        ref={(r) => (this.tableRef = r)}
      />
    ) : (
      this.renderScrollableTable(plainTable, alternateColoredRows, commonProps, manualProps)
    );
  }

  render() {
    const { isFullPageTable } = this.props;

    return (
      <div
        className={classnames('table-container', {
          'full-page-table-container': isFullPageTable,
        })}
      >
        {this.props.customTopBar}
        <div
          className={classnames('table-container__table', {
            table_disabled: this.props.disabled,
          })}
        >
          {this.renderTable()}
        </div>
      </div>
    );
  }
}
